import { supabase } from "@/integrations/supabase/client";
import { applyVisibleDocumentFilters } from "@/stores/documents/repository/queries";
import type {
  FolderOrganizeRequest,
  OrganizeApplyResult,
  OrganizeDimension,
  OrganizeScope,
  OrganizeScopeCounts,
  ProposalDocument,
  TagProposalPreview,
} from "../types/organize";
import { ACTIVE_ORGANIZE_STATUSES, RESUMABLE_ORGANIZE_STATUSES } from "../types/organize";
import { applyProjectFolderFilters, isSystemFolderName } from "./folderService";

const REQUEST_COLUMNS =
  "id, org_id, building_id, project_id, folder_id, scope, mode, dimensions, language, instructions, status, proposal, apply_result, error, created_at, completed_at, expires_at";

/**
 * Folder ids inside the subtree of `rootId` (null = whole project), excluding
 * the root itself, system-managed folders (WHA-3270, e.g. "Issue Attachments"
 * — read-only, never reorganized), and everything nested under them.
 *
 * Walks parent_id links rather than folder_path so legacy folders with no
 * folder_path populated are still found. A broken parent chain (deleted or
 * cross-project parent) counts as top-level; a parent_id cycle drops the
 * folder.
 */
export function collectSubtreeFolderIds(
  folders: Array<{ id: number; parent_id: number | null; name: string | null }>,
  rootId: number | null
): number[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const inSubtree = (folder: (typeof folders)[number]): boolean => {
    const seen = new Set<number>();
    let cur: (typeof folders)[number] | undefined = folder;
    while (cur && !seen.has(cur.id)) {
      if (isSystemFolderName(cur.name)) return false;
      // null === null covers top-level folders under the project root.
      if (cur.parent_id === rootId) return true;
      if (cur.parent_id === null) return false;
      seen.add(cur.id);
      cur = byId.get(cur.parent_id);
    }
    return cur === undefined && rootId === null;
  };
  return folders.filter((f) => f.id !== rootId && inSubtree(f)).map((f) => f.id);
}

export const organizeService = {
  /**
   * Queue an organise run. folderId null targets the PROJECT ROOT (loose
   * documents with no folder); projectId is required in that case.
   * Returns the new request id.
   */
  async requestOrganization(
    folderId: number | null,
    scope: OrganizeScope,
    instructions: string | undefined,
    projectId: number
  ): Promise<number> {
    const trimmed = instructions?.trim();
    const { data, error } = await supabase.rpc("request_folder_organization", {
      ...(folderId !== null ? { p_folder_id: folderId } : { p_project_id: projectId }),
      p_scope: scope,
      ...(trimmed ? { p_instructions: trimmed } : {}),
    });
    if (error) throw error;
    return data as number;
  },

  /** Cancel a pending request or discard a ready proposal. */
  async cancelRequest(requestId: number): Promise<void> {
    const { error } = await supabase.rpc("cancel_organize_request", {
      p_request_id: requestId,
    });
    if (error) throw error;
  },

  /**
   * The structure the deterministic mode would produce, WITHOUT creating a
   * request row. Drives the live preview as the user reorders dimensions;
   * because it is the same SQL that request_tag_organization stores, the
   * preview can never disagree with what is applied.
   */
  async previewTagOrganization(
    folderId: number | null,
    projectId: number,
    scope: OrganizeScope,
    dimensions: OrganizeDimension[],
    language: string
  ): Promise<TagProposalPreview> {
    const { data, error } = await supabase.rpc("preview_tag_organization", {
      p_folder_id: folderId,
      p_project_id: projectId,
      p_scope: scope,
      p_dimensions: dimensions,
      p_language: language,
    });
    if (error) throw error;
    return data as TagProposalPreview;
  },

  /** Queue a deterministic tag run. Returns ready immediately — no agent job. */
  async requestTagOrganization(
    folderId: number | null,
    projectId: number,
    scope: OrganizeScope,
    dimensions: OrganizeDimension[],
    language: string
  ): Promise<number> {
    const { data, error } = await supabase.rpc("request_tag_organization", {
      p_folder_id: folderId,
      p_project_id: projectId,
      p_scope: scope,
      p_dimensions: dimensions,
      p_language: language,
    });
    if (error) throw error;
    return data as number;
  },

  /**
   * Apply a ready proposal server-side (atomically), minus unchecked documents.
   * folderNameOverrides carries the reviewer's renames keyed by proposal folder
   * key; the RPC validates them like any other name and ignores any naming a
   * reused existing folder.
   */
  async applyProposal(
    requestId: number,
    excludedDocumentIds: number[],
    folderNameOverrides: Record<string, string> = {},
    assignmentOverrides: Record<string, string> = {}
  ): Promise<OrganizeApplyResult> {
    const { data, error } = await supabase.rpc("apply_organize_proposal", {
      p_request_id: requestId,
      p_excluded_document_ids: excludedDocumentIds,
      p_folder_name_overrides: folderNameOverrides,
      // The files the reviewer dragged onto another proposed folder. Validated
      // server-side like the proposal itself: a destination the proposal does
      // not contain, or a document it never mentioned, is refused rather than
      // quietly dropped.
      p_assignment_overrides: assignmentOverrides,
    });
    if (error) throw error;
    return data as OrganizeApplyResult;
  },

  async getRequest(requestId: number): Promise<FolderOrganizeRequest | null> {
    const { data, error } = await supabase
      .from("folder_organize_requests")
      .select(REQUEST_COLUMNS)
      .eq("id", requestId)
      .maybeSingle();
    if (error) throw error;
    return data as FolderOrganizeRequest | null;
  },

  /**
   * The request the dialog should resume into for a folder: the newest run
   * still in flight or awaiting review, even when a later row for the same
   * folder already finished (two tabs racing can leave an active row behind a
   * newer applied/cancelled one). With no active run, the latest run is
   * returned only if it failed/expired — so the user sees the outcome after a
   * reload — and an old failure stays hidden behind a newer successful apply.
   *
   * Scoped to the CALLER's own runs, like OrganizeWatcher and the
   * awaiting-review count: a teammate's run on the same folder carries their
   * free-text instructions and is theirs to apply or discard, so resuming into
   * it would both leak that text and hand their proposal to the wrong person.
   */
  async getActiveRequestForFolder(
    folderId: number | null,
    projectId: number,
    requestedBy: number
  ): Promise<FolderOrganizeRequest | null> {
    const scoped = () => {
      const q = supabase
        .from("folder_organize_requests")
        .select(REQUEST_COLUMNS)
        .eq("project_id", projectId)
        .eq("requested_by", requestedBy);
      return folderId !== null ? q.eq("folder_id", folderId) : q.is("folder_id", null);
    };

    const { data: active, error: activeError } = await scoped()
      .in("status", [...ACTIVE_ORGANIZE_STATUSES])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (activeError) throw activeError;
    if (active) return active as FolderOrganizeRequest;

    const { data, error } = await scoped()
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const request = data as FolderOrganizeRequest | null;
    return request && RESUMABLE_ORGANIZE_STATUSES.includes(request.status) ? request : null;
  },

  /**
   * Document counts for the launch modal's scope choice. The modal's "{count}
   * files" hint and the Start gate must describe the rows the user can see in
   * the folder, so both the folder set and the document set go through the
   * same predicates the tree and the browse list use —
   * `applyProjectFolderFilters` and `applyVisibleDocumentFilters` — rather
   * than a second copy of them here. Chat attachments are excluded on top:
   * they are the backend's rule, and `hidden` already drops the ones still
   * sitting in the hidden chatbot project.
   *
   * folderId null = project root: loose counts documents with no folder,
   * subtree counts the whole project.
   */
  async getScopeCounts(folderId: number | null, projectId: number): Promise<OrganizeScopeCounts> {
    const folderQuery = supabase
      .from("document_folders")
      .select("id, parent_id, name")
      .eq("project_id", projectId);
    const { data: folders, error: foldersError } = await applyProjectFolderFilters(folderQuery);
    if (foldersError) throw foldersError;
    const descendantIds = collectSubtreeFolderIds(folders ?? [], folderId);

    // NOT (col IS TRUE) matches NULL and FALSE; distinct-column filters
    // AND reliably, unlike two chained .or() params.
    const countDocs = async (folderIds: number[], includeRoot: boolean): Promise<number> => {
      let total = 0;
      if (includeRoot) {
        const rootQuery = supabase
          .from("documents")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId)
          .is("folder_id", null)
          .not("chatbot_attachment", "is", true);
        const { count, error } = await applyVisibleDocumentFilters(rootQuery);
        if (error) throw error;
        total += count ?? 0;
      }
      // PostgREST caps URL length; chunk like getDocumentNames for big subtrees.
      for (let i = 0; i < folderIds.length; i += 200) {
        const chunk = folderIds.slice(i, i + 200);
        const chunkQuery = supabase
          .from("documents")
          .select("id", { count: "exact", head: true })
          .in("folder_id", chunk)
          .not("chatbot_attachment", "is", true);
        const { count, error } = await applyVisibleDocumentFilters(chunkQuery);
        if (error) throw error;
        total += count ?? 0;
      }
      return total;
    };

    const isRoot = folderId === null;
    const [looseDocuments, subtreeDocuments] = await Promise.all([
      isRoot ? countDocs([], true) : countDocs([folderId], false),
      isRoot ? countDocs(descendantIds, true) : countDocs([folderId, ...descendantIds], false),
    ]);

    return { looseDocuments, subtreeDocuments, subfolders: descendantIds.length };
  },

  /**
   * The rows the proposal review list renders. Filename alone was enough while
   * the modal showed plain text; it now draws the Documents page's own row —
   * file-type icon, size, when — and offers the same eye-icon preview, which
   * needs the file's path and the state `canPreviewDocumentRow` reads.
   */
  async getProposalDocuments(documentIds: number[]): Promise<Map<number, ProposalDocument>> {
    if (documentIds.length === 0) return new Map();
    const rows = new Map<number, ProposalDocument>();
    // PostgREST caps URL length; chunk defensively for very large proposals.
    for (let i = 0; i < documentIds.length; i += 200) {
      const chunk = documentIds.slice(i, i + 200);
      const { data, error } = await supabase
        .from("documents")
        .select(
          "id, filename, file_path, building_id, content_type, size, state, created_at, " +
            "document_content_modified_date, original_created_at, version, parent_document_id"
        )
        .in("id", chunk);
      if (error) throw error;
      for (const row of data ?? []) rows.set(row.id, row as ProposalDocument);
    }
    return rows;
  },
};
